/// <amd-module name="Analytics/Boards/BoardsQueries/IterationsQuery" />
import { AnalyticsCacheableQueryBase, CommonFeatureCommandName } from "Analytics/Common/AnalyticsCacheableQueryBase";
import { ODataQueryOptions } from "Analytics/Common/ODataQueryOptions";
import { latestODataVersion, QueryUtilities } from "Analytics/Common/Utilities/QueryUtilities";

/**
 * A subset of iteration information exposed by the requested query.
 * Some properties will be undefined, if the pertinent query does not expose them.
 */
export interface Iteration {
    IterationSK: string;
    IterationName: string;

    /** Start of iteration: Formatted as a date string with Account Time Zone offset */
    StartDateTimeOffset: string;

    /** End of iteration: Formatted as a date string with Account Time Zone offset */
    EndDateTimeOffset: string;

    /** Iteration path: Comes in as string delimited by '\\' */
    IterationPath: string;

    /** True when the current date is beyond the iteration end date. **/
    IsEnded: boolean;

    /** Project GUID. */
    ProjectSK?: string;

    Number?: number;

    Depth?: number;
}

/*
 * ======================================== Analytics Wire Data Models ==========================================
 * The following interfaces do not match 1-to-1 with the names of the entities in the analytics service,
 * nor do they expose all of the properties of those entities. These interfaces only expose what
 * we query for from the analytics service.
 */
export interface ODataIteration {
    IterationSK: string;
    IterationName: string;

    /** Start of iteration: Formatted as a date string with Account Time Zone offset */
    StartDate: string;
    /** End of iteration: Formatted as a date string with Account Time Zone offset */
    EndDate: string;

    /** Iteration path: Comes in as string delimited by '\\' */
    IterationPath: string;

    /** True when the current date is beyond the iteration end date **/
    IsEnded: boolean;

    /** Project GUID. */
    ProjectSK?: string;

    Number?: number;

    Depth?: number;
}

export class IterationsQuery extends AnalyticsCacheableQueryBase<Iteration[]> {
    public static defaultColumns = "IterationSK, IterationName, StartDate, EndDate, IsEnded, IterationPath";
    public static unrestrictedColumns = "IterationSK,IterationName,StartDate,EndDate,IterationPath,Depth,ProjectSK,Number,Depth";
    public static generateQueryOptions(
        projectId: string,
        teamId: string | undefined,
        numberOfIterations: number | undefined,
        startDateFilter: boolean,
        properties: string
    ): ODataQueryOptions {
        let $top: string | undefined = undefined;
        let $filter: string | undefined = undefined;

        // if a bounded set of iterations are being queried for immediate rendering, ignore future dates.
        // Otherwise, allow all iterations with start dates, for use in config mode.
        const ignoreIterationsStartingInFuture = numberOfIterations != null;
        let dateFilter: string | undefined = undefined;
        if (startDateFilter) {
            dateFilter = ignoreIterationsStartingInFuture ? `StartDate lt now()` : `StartDate ne null`;
        }

        const filterTeams = `Teams/any(t:t/TeamSK eq ${teamId})`;
        if (teamId) {
            $filter = filterTeams + " and " + dateFilter;
        } else {
            $filter = dateFilter;
        }

        // $orderby
        const $orderby = `IsEnded,StartDate desc,EndDate desc,IterationName`;

        // $top
        if (numberOfIterations) {
            $top = `${numberOfIterations}`;
        }

        // $select
        const $select = properties;

        if (!$filter && !projectId) {
            return {
                entityType: "Iterations",
                oDataVersion: latestODataVersion,
                $orderby: $orderby,
                $select: $select
            };
        }

        return {
            entityType: "Iterations",
            oDataVersion: latestODataVersion,
            project: projectId,
            $filter: $filter,
            $orderby: $orderby,
            $top: $top,
            $select: $select
        };
    }

    /**
     * Constructs a team-scoped query to gather a *list* of recent iterations for immediate rendering on view.
     * @param projectId - The projectId to scope the query on.
     * @param teamId - The team to constrain the query against.
     * @param numberOfIterations - Constrains the number of historical iterations to pull, going backwards from the present. Used in velocity view use case. Iterations starting in the future are ignored.
     */
    public static onTeam(projectId: string, teamId: string, numberOfIterations: number): IterationsQuery {
        const columns = IterationsQuery.defaultColumns;
        return new IterationsQuery(projectId, teamId, numberOfIterations, true, columns);
    }

    /**
     * Constructs a team-agnostic iteration query on the specified project, to feed iteration picker control in widget configs.
     * Filters away any iterations with unset start dates.
     * @param projectId - The projectId to scope the query on.
     */
    public static onProject(projectId: string): IterationsQuery {
        const columns = IterationsQuery.defaultColumns;
        return new IterationsQuery(projectId, undefined, undefined, true, columns);
    }

    /** Constructs a query for all iterations on the specified project, unfiltered, and exposing an extended set of columns. Only relevant to advanced scenarios like Ax Views.
     * @param projectId - The projectId to scope the query on.
     */
    public static allIterationsOnProject(projectId: string): IterationsQuery {
        const columns = IterationsQuery.unrestrictedColumns;
        return new IterationsQuery(projectId, undefined, undefined, false, columns);
    }

    private queryName?: string;
    // Visibility of constructor is restricted to force callers to be conscious about their use.
    protected constructor(
        projectId: string,
        teamId: string | undefined,
        numberOfIterations: number | undefined,
        startDateFilter: boolean,
        properties: string,
        queryName?: string
    ) {
        super(CommonFeatureCommandName, IterationsQuery.generateQueryOptions(projectId, teamId, numberOfIterations, startDateFilter, properties));
        this.queryName = queryName;
    }

    public getQueryName(): string {
        return this.queryName || "IterationsQuery";
    }

    /**  Override default behavior, by reversing results  */
    protected interpretQueryResults(data: { value: ODataIteration[] }): Iteration[] {
        return interpretQueryResults(data);
    }
}

/** A focused query to extract information about specific iterations. */
export class IterationQuery extends AnalyticsCacheableQueryBase<Iteration[]> {
    /**
     * Creates a query targeted to a specific iteration.
     * @param projectId - The projectId to scope the query on.
     * @param iterationPath - The iterationPath, delimited by '\\'
     */
    public static onIterationPath(projectId: string, iterationPath: string): IterationQuery {
        const $filter = `IterationPath eq '${QueryUtilities.escapeString(iterationPath)}'`; // Iteration paths are allowed to have single quotes in their name. we have to escape them for the odata endpoint to recognize.
        const $select = IterationsQuery.defaultColumns;
        const queryOptions = {
            entityType: "Iterations",
            oDataVersion: latestODataVersion,
            project: projectId,
            $filter: $filter,
            $select: $select
        };

        return new IterationQuery(queryOptions);
    }

    private constructor(options: ODataQueryOptions) {
        super(CommonFeatureCommandName, options);
    }

    public getQueryName(): string {
        return "IterationQuery";
    }

    /**  Override default behavior, by reversing results  */
    protected interpretQueryResults(data: { value: ODataIteration[] }): Iteration[] {
        return interpretQueryResults(data);
    }
}

function interpretQueryResults(data: { value: ODataIteration[] }): Iteration[] {
    return data.value
        .map<Iteration>(odataIteration => {
            return {
                IterationSK: odataIteration.IterationSK,
                IterationName: odataIteration.IterationName,
                IterationPath: odataIteration.IterationPath,
                StartDateTimeOffset: odataIteration.StartDate,
                EndDateTimeOffset: odataIteration.EndDate,
                IsEnded: odataIteration.IsEnded,

                // Extended Props which are not essential for picker oriented scenarios.
                ProjectSK: odataIteration.ProjectSK,
                Number: odataIteration.Number,
                Depth: odataIteration.Depth
            };
        })
        .reverse();
}
